import { observer } from 'mobx-react';
import * as React from 'react';
import {
    collectReconciled,
    EXPORT_PRESETS,
    ExportPreset,
    IReconciledExport,
    exportPresetRange,
    reconciledCsv,
    reconciledCsvFilename,
} from '../../services/remits/reconcile-export';
import { ReconcileStore } from '../../stores/remits/reconcile.store';
import { ButtonGroup, IButtonGroupItemProps } from '../base/button-group/button-group';
import { BaseButton } from '../base/button/button';
import { BaseCheckbox } from '../base/checkbox/checkbox';
import { BaseInput } from '../base/input/input';
import { Notice } from '../base/notice/notice';
import { Spinner } from '../base/spinner/spinner';
import { money, signedMoney } from './remit-format';
import * as S from './reconcile.styles';

/** Saves the file — the Denials export's way: a Blob, a link attached for the click, released a moment later. */
const download = (text: string, filename: string): void => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10000);
};

type Phase = { name: 'idle' } | { name: 'collecting'; done: number; total: number } | { name: 'ready'; result: IReconciledExport } | { name: 'failed'; message: string };

/**
 * Export reconciled transactions (FE-163, crosswalk §6). Two steps rather than one button: a year of deposits is dozens of
 * calls, so the file is gathered first with a running count, previewed, and only then saved.
 */
export const ReconcileExportDialog: React.FC<{
    store: ReconcileStore;
    clearinghouseName: (id: string | undefined) => string | undefined;
    onClose: () => void;
}> = observer(({ store, clearinghouseName, onClose }) => {
    const initial = exportPresetRange('thisMonth', new Date());
    const [preset, setPreset] = React.useState<ExportPreset>('thisMonth');
    const [from, setFrom] = React.useState(initial?.from || '');
    const [to, setTo] = React.useState(initial?.to || '');
    const [chosen, setChosen] = React.useState<string[]>(() => store.accounts.map(account => account.itemId));
    const [phase, setPhase] = React.useState<Phase>({ name: 'idle' });
    const [saved, setSaved] = React.useState<string>();

    const range = from && to ? (from <= to ? { from, to } : { from: to, to: from }) : undefined;
    const presetButtons: IButtonGroupItemProps[] = EXPORT_PRESETS.map(each => ({ label: each.label }));
    const busy = phase.name === 'collecting';

    const choosePreset = (id: ExportPreset): void => {
        const days = exportPresetRange(id, new Date());
        setPreset(id);
        if (days) {
            setFrom(days.from);
            setTo(days.to);
        }
    };
    // Typing a date makes it a custom range (crosswalk §6).
    const typeDate = (set: (value: string) => void) => (event: React.ChangeEvent<HTMLInputElement>): void => {
        set(event.target.value);
        setPreset('custom');
    };

    // Anything that changes what the file holds puts it back to "not prepared", and a collection still running for the
    // old choice can no longer report into this one.
    const run = React.useRef(0);
    React.useEffect(() => {
        run.current++;
        setPhase({ name: 'idle' });
        setSaved(undefined);
    }, [from, to, chosen]);

    const prepare = async (): Promise<void> => {
        if (!range) {
            return;
        }
        const mine = ++run.current;
        const report = (next: Phase): void => {
            if (run.current === mine) {
                setPhase(next);
            }
        };
        report({ name: 'collecting', done: 0, total: 0 });
        try {
            const result = await collectReconciled({
                banking: store.banking,
                remits: store.remits,
                accounts: store.accounts.filter(account => chosen.includes(account.itemId)),
                from: range.from,
                to: range.to,
                clearinghouseName,
                onProgress: (done, total) => report({ name: 'collecting', done, total }),
            });
            report({ name: 'ready', result });
        } catch (err) {
            report({ name: 'failed', message: err instanceof Error && err.message ? err.message : 'The deposits could not be read.' });
        }
    };

    const result = phase.name === 'ready' ? phase.result : undefined;
    const incomplete = !!result?.unsureDays.length;

    return (
        <S.ExportBody data-testid="reconcile-export">
            {/* The BANK date: "posted" on this page means linked to a remit, and the filter is not on that. */}
            <S.ExportLabel>Bank transaction date</S.ExportLabel>
            <ButtonGroup
                variant="track"
                buttons={presetButtons}
                selected={presetButtons[EXPORT_PRESETS.findIndex(each => each.id === preset)]}
                onSelect={(button): void => choosePreset(EXPORT_PRESETS.find(each => each.label === button.label)?.id || 'thisMonth')}
            />
            <S.ExportDates>
                <S.Slot width={170}>
                    <BaseInput compact={true} type="date" aria-label="From" value={from} onChange={typeDate(setFrom)} />
                </S.Slot>
                <S.Slot width={170}>
                    <BaseInput compact={true} type="date" aria-label="To" value={to} onChange={typeDate(setTo)} />
                </S.Slot>
            </S.ExportDates>
            <S.ExportLabel>Bank accounts</S.ExportLabel>
            {store.accounts.map(account => (
                <BaseCheckbox
                    key={account.itemId}
                    label={`${account.label}${String(account.itemType).toUpperCase() === 'MANUAL' ? ' · Manual' : ' · Plaid'}`}
                    checked={chosen.includes(account.itemId)}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                        setChosen(current =>
                            event.target.checked ? [...current, account.itemId] : current.filter(id => id !== account.itemId),
                        )
                    }
                />
            ))}
            {!chosen.length && <S.ExportHint>Select at least one account.</S.ExportHint>}
            {phase.name === 'collecting' && (
                <S.ExportHint role="status">
                    <Spinner size="14px" />{' '}
                    {phase.total ? `Read ${phase.done.toLocaleString()} of ${phase.total.toLocaleString()} days…` : 'Reading the posted deposits…'}
                </S.ExportHint>
            )}
            {phase.name === 'failed' && <Notice tone="attention">{phase.message}</Notice>}
            {result && !result.rows.length && <S.ExportHint>No reconciled transactions in this range.</S.ExportHint>}
            {result && result.rows.length > 0 && (
                <S.ExportPreview data-testid="reconcile-export-preview">
                    <div>
                        <dt>Transactions</dt>
                        <dd>{result.deposits.toLocaleString()}</dd>
                    </div>
                    <div>
                        <dt>Deposits total</dt>
                        <dd>{money(result.depositsTotal)}</dd>
                    </div>
                    <div>
                        <dt title="Deposit minus what is posted to it, summed. Plus and minus cancel: the count below is the one to check.">
                            Variance
                        </dt>
                        <dd>{signedMoney(result.variance)}</dd>
                    </div>
                </S.ExportPreview>
            )}
            {result && result.depositsWithVariance > 0 && (
                <S.ExportHint>
                    {result.depositsWithVariance.toLocaleString()} {result.depositsWithVariance === 1 ? 'deposit does' : 'deposits do'} not
                    match what is posted to {result.depositsWithVariance === 1 ? 'it' : 'them'}.
                </S.ExportHint>
            )}
            {result && result.unmatched > 0 && (
                <S.ExportHint>
                    {result.unmatched.toLocaleString()} posted {result.unmatched === 1 ? 'deposit has' : 'deposits have'} no remit this
                    console can read. {result.unmatched === 1 ? 'It is' : 'They are'} in the file with the remit columns blank.
                </S.ExportHint>
            )}
            {incomplete && result && (
                <Notice tone="warning">
                    Deposits may be missing for {result.unsureDays.join(', ')}: {result.unsureDays.length === 1 ? 'that day has' : 'those days have'}{' '}
                    more than the bank list returns at once, and its pages can skip a row. Check{' '}
                    {result.unsureDays.length === 1 ? 'it' : 'them'} against the statement. The file name ends in _INCOMPLETE.
                </Notice>
            )}
            {saved && <S.ExportHint role="status">{saved}</S.ExportHint>}
            <S.ExportActions>
                <BaseButton variant="outlined" size="compact" disabled={busy} onClick={onClose}>
                    Close
                </BaseButton>
                {result ? (
                    <BaseButton
                        variant="primary"
                        size="compact"
                        disabled={!result.rows.length || !range}
                        onClick={(): void => {
                            if (range) {
                                const filename = reconciledCsvFilename(range.from, range.to, incomplete);
                                download(reconciledCsv(result.rows), filename);
                                setSaved(`${result.rows.length.toLocaleString()} rows exported to ${filename}.`);
                            }
                        }}>
                        Download CSV
                    </BaseButton>
                ) : (
                    <BaseButton variant="primary" size="compact" disabled={busy || !range || !chosen.length} onClick={(): void => void prepare()}>
                        {busy ? 'Exporting…' : 'Export CSV'}
                    </BaseButton>
                )}
            </S.ExportActions>
        </S.ExportBody>
    );
});
